import deepEqual from 'fast-deep-equal';
import * as yup from 'yup';
import {
  DeviceSignalKind,
  type DeviceSignalInput,
  type DeviceSignalPatch,
  type DeviceSignalTarget,
} from '~/generated/graphql';
import { i18n } from '~/lib/i18n';
import { EVENT_VALUES, isEventName } from './utils';

export type Action =
  | { command: 'arbitrary'; arbitrary: string }
  | { command: 'play_overlay'; id: number }
  | { command: 'play_show'; id: number }
  | { command: 'reboot' }
  | { command: 'stop_overlay' }
  | { command: 'trigger_view'; id: number }
  | { command: 'tune_channel'; id: number };

/**
 * Rerpesents any type of (nullable) entity ID in our forms.
 * Forms have strings, so empty string (and only a zero-length string) is turned to null.
 * Nulls still won't be valid unless you chain `.nullable()`.
 *
 * @internal
 */
export const idSchema = yup
  .number()
  .integer()
  .positive()
  .transform((val: unknown, originalVal: unknown) => (originalVal === '' ? null : val));

/** @internal */
export const kindSchema = yup.string().oneOf(Object.values(DeviceSignalKind));

const commands = [
  'arbitrary',
  'play_overlay',
  'play_show',
  'reboot',
  'stop_overlay',
  'trigger_view',
  'tune_channel',
] as const;

const labels = {
  play_overlay: 'Overlay',
  play_show: 'Show',
  trigger_view: 'View',
  tune_channel: 'Channel',
} as const;

/**
 * We need to cast this (and use tests) to ensure that this actually matches
 * a schema that yup can't infer.
 *
 * @internal
 */
export const actionSchema = yup
  .object({
    command: yup
      .string()
      .oneOf(commands)
      .required(i18n.t('errors.validation.required'))
      .label(i18n.t('pages.device_signals.settings.validation.action_label')),
  })
  .default(undefined)
  .when((values, schema) => {
    const value: unknown = values.at(0);

    // Let `.required()` handle this case
    if (value === undefined) return schema;

    const command =
      typeof value === 'object' && value !== null && 'command' in value ? value.command : undefined;

    if (command === 'arbitrary')
      return schema.shape({
        arbitrary: yup.string().required(i18n.t('errors.validation.required')),
      });

    if (command === 'stop_overlay' || command === 'reboot') return schema;

    // Everything else uses an ID
    if ((command as string) in labels)
      return schema.shape({
        id: idSchema
          .required(i18n.t('errors.validation.required'))
          .label(labels[command as keyof typeof labels]),
      });

    // This should never happen, fail unconditionally
    return schema.test(() => false);
  }) as unknown as yup.ObjectSchema<Action | undefined>;

type SignalEvent =
  // Events with any offset (negative or positive)
  | { name: 'COMMON_START_TIME'; offset: number }
  // Events with positive offset only
  | { name: 'CHL_END_GAME'; offset: number }
  | { name: 'MILB_END_GAME'; offset: number }
  | { name: 'MLB_END_GAME'; offset: number }
  | { name: 'MLS_END_GAME'; offset: number }
  | { name: 'NBA_END_GAME'; offset: number }
  | { name: 'NCAAF_END_TIME'; offset: number }
  | { name: 'NFL_END_TIME'; offset: number }
  | { name: 'NHL_END_GAME'; offset: number }
  | { name: 'NWSL_END_GAME'; offset: number }
  // CHL events
  | { name: 'CHL_GOAL' }
  | { name: 'CHL_HOME_TEAM_LOSS' }
  | { name: 'CHL_HOME_TEAM_WIN' }
  | { name: 'CHL_OVERTIME' }
  | { name: 'CHL_START_1ST_INTERMISSION' }
  | { name: 'CHL_START_2ND_INTERMISSION' }
  | { name: 'CHL_START_PERIOD_2' }
  | { name: 'CHL_START_PERIOD_3' }
  // MiLB events
  | { name: 'MILB_1ST_INNING_START' }
  | { name: 'MILB_1ST_INNING_MIDDLE' }
  | { name: 'MILB_1ST_INNING_END' }
  | { name: 'MILB_2ND_INNING_START' }
  | { name: 'MILB_2ND_INNING_MIDDLE' }
  | { name: 'MILB_2ND_INNING_END' }
  | { name: 'MILB_3RD_INNING_START' }
  | { name: 'MILB_3RD_INNING_MIDDLE' }
  | { name: 'MILB_3RD_INNING_END' }
  | { name: 'MILB_4TH_INNING_START' }
  | { name: 'MILB_4TH_INNING_MIDDLE' }
  | { name: 'MILB_4TH_INNING_END' }
  | { name: 'MILB_5TH_INNING_START' }
  | { name: 'MILB_5TH_INNING_MIDDLE' }
  | { name: 'MILB_5TH_INNING_END' }
  | { name: 'MILB_6TH_INNING_START' }
  | { name: 'MILB_6TH_INNING_MIDDLE' }
  | { name: 'MILB_6TH_INNING_END' }
  | { name: 'MILB_7TH_INNING_START' }
  | { name: 'MILB_7TH_INNING_MIDDLE' }
  | { name: 'MILB_7TH_INNING_END' }
  | { name: 'MILB_8TH_INNING_START' }
  | { name: 'MILB_8TH_INNING_MIDDLE' }
  | { name: 'MILB_8TH_INNING_END' }
  | { name: 'MILB_9TH_INNING_START' }
  | { name: 'MILB_9TH_INNING_MIDDLE' }
  | { name: 'MILB_9TH_INNING_END' }
  | { name: 'MILB_HOME_RUN' }
  | { name: 'MILB_HOME_TEAM_LOSS' }
  | { name: 'MILB_HOME_TEAM_WIN' }
  // MLB events
  | { name: 'MLB_1ST_INNING_START' }
  | { name: 'MLB_1ST_INNING_MIDDLE' }
  | { name: 'MLB_1ST_INNING_END' }
  | { name: 'MLB_2ND_INNING_START' }
  | { name: 'MLB_2ND_INNING_MIDDLE' }
  | { name: 'MLB_2ND_INNING_END' }
  | { name: 'MLB_3RD_INNING_START' }
  | { name: 'MLB_3RD_INNING_MIDDLE' }
  | { name: 'MLB_3RD_INNING_END' }
  | { name: 'MLB_4TH_INNING_START' }
  | { name: 'MLB_4TH_INNING_MIDDLE' }
  | { name: 'MLB_4TH_INNING_END' }
  | { name: 'MLB_5TH_INNING_START' }
  | { name: 'MLB_5TH_INNING_MIDDLE' }
  | { name: 'MLB_5TH_INNING_END' }
  | { name: 'MLB_6TH_INNING_START' }
  | { name: 'MLB_6TH_INNING_MIDDLE' }
  | { name: 'MLB_6TH_INNING_END' }
  | { name: 'MLB_7TH_INNING_START' }
  | { name: 'MLB_7TH_INNING_MIDDLE' }
  | { name: 'MLB_7TH_INNING_END' }
  | { name: 'MLB_8TH_INNING_START' }
  | { name: 'MLB_8TH_INNING_MIDDLE' }
  | { name: 'MLB_8TH_INNING_END' }
  | { name: 'MLB_9TH_INNING_START' }
  | { name: 'MLB_9TH_INNING_MIDDLE' }
  | { name: 'MLB_9TH_INNING_END' }
  | { name: 'MLB_HOME_RUN' }
  | { name: 'MLB_HOME_TEAM_LOSS' }
  | { name: 'MLB_HOME_TEAM_WIN' }
  // MLS events
  | { name: 'MLS_HOME_TEAM_GOAL' }
  | { name: 'MLS_HOME_TEAM_LOSS' }
  | { name: 'MLS_HOME_TEAM_WIN' }
  | { name: 'MLS_START_2ND_HALF' }
  | { name: 'MLS_START_HALFTIME' }
  // NBA events
  | { name: 'NBA_HOME_TEAM_LOSS' }
  | { name: 'NBA_HOME_TEAM_WIN' }
  | { name: 'NBA_START_HALFTIME' }
  | { name: 'NBA_START_QUARTER_2' }
  | { name: 'NBA_START_QUARTER_3' }
  | { name: 'NBA_START_QUARTER_4' }
  // NCAAF events
  | { name: 'NCAAF_END_QUARTER_1' }
  | { name: 'NCAAF_END_QUARTER_3' }
  | { name: 'NCAAF_FIELD_GOAL' }
  | { name: 'NCAAF_HOME_TEAM_LOSS' }
  | { name: 'NCAAF_HOME_TEAM_WIN' }
  | { name: 'NCAAF_START_HALFTIME' }
  | { name: 'NCAAF_START_QUARTER_2' }
  | { name: 'NCAAF_START_QUARTER_3' }
  | { name: 'NCAAF_START_QUARTER_4' }
  | { name: 'NCAAF_TOUCHDOWN' }
  // NFL events
  | { name: 'NFL_FIELD_GOAL' }
  | { name: 'NFL_HOME_TEAM_LOSS' }
  | { name: 'NFL_HOME_TEAM_WIN' }
  | { name: 'NFL_OVERTIME' }
  | { name: 'NFL_START_HALFTIME' }
  | { name: 'NFL_START_QUARTER_2' }
  | { name: 'NFL_START_QUARTER_3' }
  | { name: 'NFL_START_QUARTER_4' }
  | { name: 'NFL_TOUCHDOWN' }
  // NHL events
  | { name: 'NHL_GOAL' }
  | { name: 'NHL_HOME_TEAM_LOSS' }
  | { name: 'NHL_HOME_TEAM_WIN' }
  | { name: 'NHL_OVERTIME' }
  | { name: 'NHL_START_1ST_INTERMISSION' }
  | { name: 'NHL_START_2ND_INTERMISSION' }
  | { name: 'NHL_START_PERIOD_2' }
  | { name: 'NHL_START_PERIOD_3' }
  // NWSL events
  | { name: 'NWSL_HOME_TEAM_GOAL' }
  | { name: 'NWSL_HOME_TEAM_LOSS' }
  | { name: 'NWSL_HOME_TEAM_WIN' }
  | { name: 'NWSL_START_2ND_HALF' }
  | { name: 'NWSL_START_HALFTIME' };

// NOTE: In yup, transorms happen _before_ validation, so we use this for min/max.
const minutesToMs = (minutes: number): number => minutes * 60_000;

/** @internal */
export const eventSchema = yup
  .object({
    name: yup
      .string()
      .oneOf(EVENT_VALUES)
      .required(i18n.t('errors.validation.required'))
      .label(i18n.t('pages.device_signals.settings.validation.event_label')),
  })
  .default(undefined)
  .when((values, schema) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const maybeName: unknown = values.at(0)?.name;

    if (!isEventName(maybeName)) return schema;

    if (maybeName === 'COMMON_START_TIME')
      return schema.shape({
        offset: yup
          .number()
          .integer()
          .min(
            minutesToMs(-600),
            i18n.t('pages.device_signals.settings.validation.offset_min_negative_600'),
          )
          .max(minutesToMs(15), i18n.t('pages.device_signals.settings.validation.offset_max_15'))
          .required(i18n.t('errors.validation.required'))
          .transform(minutesToMs)
          .label('Offset'),
      });

    if (
      maybeName === 'CHL_END_GAME' ||
      maybeName === 'MILB_END_GAME' ||
      maybeName === 'MLB_END_GAME' ||
      maybeName === 'MLS_END_GAME' ||
      maybeName === 'NBA_END_GAME' ||
      maybeName === 'NCAAF_END_TIME' ||
      maybeName === 'NFL_END_TIME' ||
      maybeName === 'NHL_END_GAME' ||
      maybeName === 'NWSL_END_GAME'
    )
      return schema.shape({
        offset: yup
          .number()
          .integer()
          .min(minutesToMs(0), i18n.t('pages.device_signals.settings.validation.offset_min_0'))
          .max(minutesToMs(600), i18n.t('pages.device_signals.settings.validation.offset_max_600'))
          .required(i18n.t('errors.validation.required'))
          .transform(minutesToMs)
          .label('Offset'),
      });

    // For all other events, don't allow extra properties like offset
    return schema.exact();
  }) as unknown as yup.ObjectSchema<SignalEvent | undefined>;

const hasOneTargetId = (value: unknown) => {
  // We let `.required()` handle this
  if (value === undefined) return true;

  if (typeof value !== 'object' || value === null) return false;

  // all devices or collection ID is present XOR group ID is present
  return (
    deepEqual(value, { allDevices: true }) ||
    'deviceDynamicCollectionId' in value !== 'deviceGroupId' in value
  );
};

/**
 * We need to cast this (and use tests) to ensure that this actually matches
 * a schema that yup can't infer.
 *
 * @internal
 */
export const targetSchema = yup
  .object({
    allDevices: yup.boolean().isTrue(),
    deviceDynamicCollectionId: idSchema,
    deviceGroupId: idSchema,
  })
  .nonNullable()
  .default(undefined)
  .test({
    name: 'has-one-target-id',
    test: hasOneTargetId,
    message: '${path} must be all devices, a device group or a device collection',
  }) as unknown as yup.ObjectSchema<DeviceSignalTarget | undefined>;

// We need to enrich the action here because GQL just has JSON
export type SignalInput = DeviceSignalInput & { action: Action } & (
    | { kind: Exclude<DeviceSignalKind, 'MANUAL'> }
    | { kind: 'EVENT'; event: SignalEvent; eventTypeId: number }
  );

export const signalInputSchema: yup.ObjectSchema<SignalInput> = yup
  .object({
    action: actionSchema
      .required(i18n.t('errors.validation.required'))
      .label(i18n.t('pages.device_signals.settings.validation.action_label')),
    enabled: yup.boolean().label(i18n.t('common.status.enabled')),
    kind: kindSchema
      .required(i18n.t('errors.validation.required'))
      .label(i18n.t('pages.device_signals.settings.validation.type_label')),
    name: yup
      .string()
      .required(i18n.t('errors.validation.required'))
      .label(i18n.t('pages.device_signals.settings.validation.name_label')),
    networkId: idSchema
      .required(i18n.t('errors.validation.required'))
      .label(i18n.t('pages.device_signals.settings.validation.network_id_label')),
    target: targetSchema
      .required(i18n.t('errors.validation.required'))
      .label(i18n.t('pages.device_signals.settings.validation.target_label')),
  })
  .when((values, schema) => {
    const value: unknown = values.at(0);

    const kind =
      typeof value === 'object' &&
      value !== null &&
      'kind' in value &&
      typeof value.kind === 'string'
        ? value.kind
        : undefined;

    // if kind is not event, event/eventTypeId are forbidden (?)
    if (kind !== 'EVENT') return schema.exact();

    // Otherwise, they are required
    return schema.shape({
      event: eventSchema
        .required(i18n.t('errors.validation.required'))
        .label(i18n.t('pages.device_signals.settings.validation.event_label')),
      eventTypeId: idSchema
        .required(i18n.t('errors.validation.required'))
        .label(i18n.t('pages.device_signals.settings.validation.event_type_label')),
    });
  }) as yup.ObjectSchema<SignalInput>;

// We need to enrich the action here because GQL just has JSON
export type SignalPatch = DeviceSignalPatch & { action?: Action | null };

export const signalPatchSchema = yup
  .object({
    action: actionSchema.label(i18n.t('pages.device_signals.settings.validation.action_label')),
    enabled: yup.boolean().label(i18n.t('common.status.enabled')),
    kind: kindSchema.label(i18n.t('pages.device_signals.settings.validation.type_label')),
    name: yup.string().label(i18n.t('pages.device_signals.settings.validation.name_label')),
    target: targetSchema.label(i18n.t('pages.device_signals.settings.validation.target_label')),
  })
  .when((values, schema) => {
    const value: unknown = values.at(0);

    const kind =
      typeof value === 'object' &&
      value !== null &&
      'kind' in value &&
      typeof value.kind === 'string'
        ? value.kind
        : undefined;

    // if kind is not present or not event, event/eventTypeId are forbidden
    if (kind !== 'EVENT') return schema.exact();

    // Otherwise, they are allowed
    return schema.shape({
      event: eventSchema
        .required(i18n.t('errors.validation.required'))
        .label(i18n.t('pages.device_signals.settings.validation.event_label')),
      eventTypeId: idSchema
        .required(i18n.t('errors.validation.required'))
        .label(i18n.t('pages.device_signals.settings.validation.event_type_label')),
    });
  }) as yup.ObjectSchema<SignalPatch>;
